import { RefObject, useLayoutEffect, useState } from 'react';

export type ModalPresentation = 'fullscreen' | 'compact';

export function measureModalPresentation(modal: HTMLElement): ModalPresentation {
  const content = modal.shadowRoot?.querySelector<HTMLElement>('[part="content"]');
  if (content?.offsetWidth && content.offsetHeight && modal.clientWidth && modal.clientHeight) {
    return content.offsetWidth >= modal.clientWidth - 1 && content.offsetHeight >= modal.clientHeight - 1 ? 'fullscreen' : 'compact';
  }
  const style = getComputedStyle(modal);
  return style.getPropertyValue('--width').trim() === '100%' && style.getPropertyValue('--height').trim() === '100%' ? 'fullscreen' : 'compact';
}

/** Inspeciona somente o modal que contém o cabeçalho, inclusive nos overlays criados por hook. */
export default function useModalPresentation(ref: RefObject<HTMLElement>, headerOnly = false) {
  const [presentation, setPresentation] = useState<ModalPresentation | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    const modal = element?.closest('ion-modal');
    if (!modal || (headerOnly && !element?.closest('ion-header'))) return;
    let observer: ResizeObserver | undefined;
    const update = () => {
      const layout = measureModalPresentation(modal);
      modal.setAttribute('data-modal-layout', layout);
      setPresentation(layout);
    };
    const observe = () => {
      observer?.disconnect();
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(update);
        observer.observe(modal);
        const content = modal.shadowRoot?.querySelector('[part="content"]');
        if (content) observer.observe(content);
      }
      update();
    };
    observe();
    modal.addEventListener('ionModalWillPresent', observe);
    modal.addEventListener('ionModalDidPresent', observe);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      modal.removeEventListener('ionModalWillPresent', observe);
      modal.removeEventListener('ionModalDidPresent', observe);
      window.removeEventListener('resize', update);
    };
  }, [ref, headerOnly]);
  return presentation;
}
